import {
  Badge,
  Body1,
  Button,
  Spinner,
  Text,
  Tooltip,
  mergeClasses,
} from '@fluentui/react-components';
import {
  Add16Filled,
  Add16Regular,
  ArrowDownload16Filled,
  ArrowDownload16Regular,
  ArrowLeft16Regular,
  ArrowUpload16Filled,
  ArrowUpload16Regular,
  bundleIcon,
  ChevronDown16Regular,
  ChevronRight16Regular,
  NotepadEdit20Regular,
} from '@fluentui/react-icons';
import { useCallback, useMemo, useState } from 'react';

import {
  DATASET_ROLE_ANNOTATOR,
  DATASET_ROLE_CONTRIBUTOR,
  DATASET_ROLE_TOOLTIP_ANNOTATOR,
} from '../../../constants/index.js';
import { useDataHubClient } from '../../../context/DataHubClientContext.js';
import { useAnnotationSchemas } from '../../../hooks/useAnnotationSchemas/useAnnotationSchemas.js';
import { useAnnotationSets } from '../../../hooks/useAnnotationSets/useAnnotationSets.js';
import { hasDatasetRole } from '../../../hooks/useMyDatasetRole/hasDatasetRole.js';
import type {
  AnnotationSchema,
  AnnotationSet,
} from '../../../services/dataHubService.interface.js';
import { DataHubService } from '../../../services/dataHubService.js';
import {
  USAGE_EXTRA_KEYS,
  withCorrelationId,
} from '../../../services/usageContext.js';
import { useScenarioFactory } from '../../../telemetry/hooks/useScenarioFactory/useScenarioFactory.js';
import { classifyScenarioError } from '../../../telemetry/scenarioError.js';
import { ScenarioName } from '../../../telemetry/scenarios.js';

import { AnnotationSetExpandedRow } from './AnnotationSets/AnnotationSetExpandedRow.js';
import { CreateAnnotationSetDialog } from './AnnotationSets/CreateAnnotationSetDialog.js';
import type { AnnotationSetsManagerProps } from './AnnotationSetsManager.interface.js';
import type { UploadProgress } from './AnnotationTransfer/annotationUploadUtils.js';
import {
  groupFilesBySubfolder,
  uploadAnnotationFolder,
} from './AnnotationTransfer/annotationUploadUtils.js';
import { DownloadAnnotationDialog } from './AnnotationTransfer/DownloadAnnotationDialog.js';
import { UploadAnnotationDialog } from './AnnotationTransfer/UploadAnnotationDialog.js';
import { useStyles } from './DatasetAnnotationsTab.style.js';

const AddIcon = bundleIcon(Add16Filled, Add16Regular) as React.FC;
const UploadIcon = bundleIcon(
  ArrowUpload16Filled,
  ArrowUpload16Regular,
) as React.FC;
const DownloadIcon = bundleIcon(
  ArrowDownload16Filled,
  ArrowDownload16Regular,
) as React.FC;

const MAX_DATA_ENTRIES_PAGE_SIZE = 100;

const BACK_TO_ANNOTATE_LABEL = 'Back to annotate';

function resolveSchemaName(
  schemaId: string,
  schemas: AnnotationSchema[],
): string {
  const found = schemas.find((s) => s.id === schemaId);
  return found?.name ?? schemaId;
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString();
}

function triggerBrowserDownload(url: string, fileName: string): void {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
}

// ── Empty state ─────────────────────────────────────────────────────────────

function AnnotationsEmptyState() {
  const styles = useStyles();

  return (
    <div className={styles.emptyStateContainer}>
      <NotepadEdit20Regular className={styles.emptyStateIcon} />
      <Text className={styles.emptyStateTitle}>No annotation sets yet</Text>
      <Text className={styles.emptyStateDescription}>
        Annotation sets organize annotations against a schema. Create one to
        start labeling data in this dataset.
      </Text>
    </div>
  );
}

// ── Expandable table view ──────────────────────────────────────────────────

interface AnnotationSetsTableProps {
  annotationSets: AnnotationSet[];
  schemas: AnnotationSchema[];
  dataSetId: string;
  dataSetVersion: number;
  service: DataHubService;
  canAnnotate: boolean;
  canPublish: boolean;
  onRefresh: () => void;
}

function AnnotationSetsTable({
  annotationSets,
  schemas,
  dataSetId,
  dataSetVersion,
  service,
  canAnnotate,
  canPublish,
  onRefresh,
}: AnnotationSetsTableProps) {
  const styles = useStyles();
  const [expandedSetId, setExpandedSetId] = useState<string | null>(null);

  const toggleRow = useCallback((annotationSetId: string) => {
    setExpandedSetId((prev) =>
      prev === annotationSetId ? null : annotationSetId,
    );
  }, []);

  return (
    <div className={styles.tableContainer}>
      <table className={styles.table}>
        <thead>
          <tr className={styles.tableHeader}>
            <th
              className={mergeClasses(
                styles.tableHeaderCell,
                styles.chevronColumnCell,
              )}
            />
            <th className={styles.tableHeaderCell}>Name</th>
            <th className={styles.tableHeaderCell}>Schema</th>
            <th className={styles.tableHeaderCell}>Status</th>
            <th className={styles.tableHeaderCell}>Version</th>
            <th className={styles.tableHeaderCell}>Created</th>
          </tr>
        </thead>
        <tbody>
          {annotationSets.map((annotationSet) => {
            const isExpanded = expandedSetId === annotationSet.annotationSetId;
            return (
              <AnnotationSetRow
                key={annotationSet.annotationSetId}
                annotationSet={annotationSet}
                schemas={schemas}
                isExpanded={isExpanded}
                onToggle={() => toggleRow(annotationSet.annotationSetId)}
                dataSetId={dataSetId}
                dataSetVersion={dataSetVersion}
                service={service}
                canAnnotate={canAnnotate}
                canPublish={canPublish}
                onRefresh={onRefresh}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Single annotation set row + expansion ───────────────────────────────────

interface AnnotationSetRowProps {
  annotationSet: AnnotationSet;
  schemas: AnnotationSchema[];
  isExpanded: boolean;
  onToggle: () => void;
  dataSetId: string;
  dataSetVersion: number;
  service: DataHubService;
  canAnnotate: boolean;
  canPublish: boolean;
  onRefresh: () => void;
}

function AnnotationSetRow({
  annotationSet,
  schemas,
  isExpanded,
  onToggle,
  dataSetId,
  dataSetVersion,
  service,
  canAnnotate,
  canPublish,
  onRefresh,
}: AnnotationSetRowProps) {
  const styles = useStyles();

  return (
    <>
      <tr
        className={mergeClasses(
          styles.tableRow,
          isExpanded ? styles.tableRowExpanded : undefined,
        )}
      >
        <td
          className={mergeClasses(styles.tableCell, styles.chevronColumnCell)}
        >
          <Button
            appearance="subtle"
            size="small"
            className={styles.chevronButton}
            icon={
              isExpanded ? <ChevronDown16Regular /> : <ChevronRight16Regular />
            }
            onClick={onToggle}
            aria-expanded={isExpanded}
            aria-label={
              isExpanded
                ? `Collapse ${annotationSet.name}`
                : `Expand ${annotationSet.name}`
            }
          />
        </td>
        <td className={styles.tableCell}>
          <Text weight="semibold">{annotationSet.name}</Text>
        </td>
        <td className={styles.tableCell}>
          <Text size={200}>
            {resolveSchemaName(annotationSet.schemaId, schemas)}
          </Text>
        </td>
        <td className={styles.tableCell}>
          <Badge
            appearance="tint"
            color={
              annotationSet.status === 'Published'
                ? 'success'
                : annotationSet.status === 'Draft'
                  ? 'informative'
                  : 'warning'
            }
            className={styles.statusBadge}
          >
            {annotationSet.status}
          </Badge>
        </td>
        <td className={styles.tableCell}>
          <Text size={200}>v{annotationSet.version}</Text>
        </td>
        <td className={styles.tableCell}>
          <Text size={200}>{formatDate(annotationSet.createdAt)}</Text>
        </td>
      </tr>
      {isExpanded && (
        <tr className={styles.expansionRow}>
          <td className={styles.expansionCell} colSpan={6}>
            <AnnotationSetExpandedRow
              annotationSet={annotationSet}
              dataSetId={dataSetId}
              dataSetVersion={dataSetVersion}
              schemas={schemas}
              service={service}
              canEdit={canAnnotate}
              canPublish={canPublish}
              onRefresh={onRefresh}
            />
          </td>
        </tr>
      )}
    </>
  );
}

// ── Manager (annotation-set explorer) ───────────────────────────────────────

export function AnnotationSetsManager({
  dataSetId,
  datasetName: _datasetName,
  dataSetVersion,
  dataSetStatus,
  myRoles,
  onBack,
}: AnnotationSetsManagerProps) {
  const styles = useStyles();
  const canAnnotate = hasDatasetRole(myRoles, DATASET_ROLE_ANNOTATOR);
  const canPublish = hasDatasetRole(myRoles, DATASET_ROLE_CONTRIBUTOR);
  const client = useDataHubClient();
  const service = useMemo(() => new DataHubService(client), [client]);
  const scenarioFactory = useScenarioFactory();

  const { annotationSets, isLoading, error, createAnnotationSet, refresh } =
    useAnnotationSets(dataSetId);

  const { schemas } = useAnnotationSchemas();

  const [createOpen, setCreateOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(
    null,
  );

  const [downloadOpen, setDownloadOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const defaultVersion =
    dataSetStatus === 'Draft' ? 'draft' : String(dataSetVersion);

  const handleCreate = useCallback(
    async (name: string, schemaId: string, description?: string) => {
      setIsCreating(true);
      setCreateError(null);
      try {
        await createAnnotationSet({ name, schemaId, description });
        setCreateOpen(false);
      } catch (err) {
        setCreateError(
          err instanceof Error
            ? err.message
            : 'Failed to create annotation set',
        );
      } finally {
        setIsCreating(false);
      }
    },
    [createAnnotationSet],
  );

  const handleUploadConfirm = useCallback(
    async (files: FileList, annotationSetName: string) => {
      setIsUploading(true);
      setUploadError(null);
      setUploadProgress(null);
      const scenario = scenarioFactory.newScenario(
        ScenarioName.DatalakeUploadAnnotations,
      );
      try {
        const matchedSet = annotationSets.find(
          (s) => s.name.toLowerCase() === annotationSetName.toLowerCase(),
        );
        if (!matchedSet) {
          setUploadError(
            `Annotation set "${annotationSetName}" not found. Create it first.`,
          );
          scenario.complete('failure', { reason: 'set_not_found' });
          return;
        }

        const subfolderFiles = groupFilesBySubfolder(files);
        if (subfolderFiles.size === 0) {
          setUploadError(
            'No annotation data found. Expected subfolders where each subfolder name matches a data entry.',
          );
          scenario.complete('failure', { reason: 'no_subfolders' });
          return;
        }

        scenario.mark('data_entries_list_started');
        const dataEntries = await service.listDataEntries({
          dataSetId,
          dataSetVersion,
          pageSize: MAX_DATA_ENTRIES_PAGE_SIZE,
          includeCount: false,
        });
        scenario.mark('data_entries_listed', {
          entry_count: dataEntries.items.length,
        });

        const result = await uploadAnnotationFolder(
          service,
          dataSetId,
          matchedSet.annotationSetId,
          matchedSet,
          schemas,
          subfolderFiles,
          dataEntries.items,
          setUploadProgress,
          scenario,
        );

        if (!result.success) {
          const summary =
            result.uploadedFileCount > 0
              ? `Uploaded ${result.uploadedFileCount} files, but some failed:\n${result.errors.join('\n')}`
              : `Upload failed:\n${result.errors.join('\n')}`;
          setUploadError(summary);
          scenario.complete('failure', {
            uploaded_files: result.uploadedFileCount,
            error_count: result.errors.length,
          });
        } else {
          setUploadOpen(false);
          scenario.complete('success', {
            uploaded_files: result.uploadedFileCount,
          });
        }
        refresh();
      } catch (err) {
        setUploadError(err instanceof Error ? err.message : 'Upload failed');
        const classification = classifyScenarioError(err);
        scenario.complete(classification.status, classification.data);
      } finally {
        setIsUploading(false);
      }
    },
    [
      service,
      dataSetId,
      dataSetVersion,
      annotationSets,
      schemas,
      refresh,
      scenarioFactory,
    ],
  );

  const handleDownloadConfirm = useCallback(
    async (version: string, annotationSetName: string) => {
      setIsDownloading(true);
      setDownloadError(null);
      // Multi-call annotation download: open one scenario, mark each
      // milestone as a mini-trace, and carry the correlation GUID on the
      // single summary TEDH call (annotationBatchGetDownloadUrls).
      const scenario = scenarioFactory.newScenario(
        ScenarioName.DatalakeDownloadAnnotations,
      );
      try {
        const matchedSet = annotationSets.find(
          (s) => s.name.toLowerCase() === annotationSetName.toLowerCase(),
        );
        if (!matchedSet) {
          setDownloadError(`Annotation set "${annotationSetName}" not found.`);
          scenario.complete('failure', { reason: 'set_not_found' });
          return;
        }
        const annotationSetId = matchedSet.annotationSetId;

        scenario.mark('annotation_entries_list_started');
        const dataResult = await service.listAnnotationData({
          dataSetId,
          annotationSetId,
          pageSize: MAX_DATA_ENTRIES_PAGE_SIZE,
          includeCount: false,
        });

        const entriesWithFiles = dataResult.items.filter(
          (entry) => entry.dataFiles && entry.dataFiles.length > 0,
        );

        scenario.mark('annotation_entries_listed', {
          entry_count: entriesWithFiles.length,
        });

        if (entriesWithFiles.length === 0) {
          setDownloadError(
            'No files found in this annotation set to download.',
          );
          scenario.complete('failure', { reason: 'no_entries' });
          return;
        }

        const batchItems = entriesWithFiles.map((entry) => ({
          dataId: entry.dataId,
        }));

        scenario.mark('annotation_download_urls_requested', {
          entry_count: entriesWithFiles.length,
        });
        const downloadResult = await service.annotationBatchGetDownloadUrls(
          dataSetId,
          annotationSetId,
          version,
          batchItems,
          // One summary usage event for the whole annotation download (D2).
          // Correlate to the annotation set; carry the number of entries pulled.
          {
            sourceId: annotationSetId,
            extras: withCorrelationId(scenario.queryId, {
              [USAGE_EXTRA_KEYS.ENTRY_COUNT]: String(entriesWithFiles.length),
            }),
          },
        );

        scenario.mark('annotation_download_urls_fetched', {
          succeeded_entries: downloadResult.succeededItems.length,
          failed_entries: downloadResult.failedItems.length,
        });

        const fileMetadataMap = new Map(
          entriesWithFiles.flatMap((entry) =>
            (entry.dataFiles ?? []).map((f) => [f.id, f]),
          ),
        );

        let downloadCount = 0;
        scenario.mark('browser_downloads_started', {
          entry_count: downloadResult.succeededItems.length,
        });
        for (const item of downloadResult.succeededItems) {
          for (const [fileId, url] of Object.entries(item.files)) {
            const meta = fileMetadataMap.get(fileId);
            const fileName = meta?.fileName ?? `${item.dataId}_${fileId}`;
            triggerBrowserDownload(url, fileName);
            downloadCount++;
          }
        }

        scenario.mark('browser_downloads_triggered', {
          downloaded_files: downloadCount,
        });

        if (downloadCount === 0) {
          setDownloadError('No download URLs were returned by the server.');
          scenario.complete('failure', { reason: 'no_urls' });
        } else if (downloadResult.failedItems.length > 0) {
          setDownloadOpen(false);
          scenario.complete('failure', {
            reason: 'partial_download',
            downloaded_files: downloadCount,
            failed_entry_count: downloadResult.failedItems.length,
          });
        } else {
          setDownloadOpen(false);
          scenario.complete('success', { downloaded_files: downloadCount });
        }
      } catch (err) {
        setDownloadError(
          err instanceof Error ? err.message : 'Download failed',
        );
        const classification = classifyScenarioError(err);
        scenario.complete(classification.status, classification.data);
      } finally {
        setIsDownloading(false);
      }
    },
    [service, dataSetId, annotationSets, scenarioFactory],
  );

  if (isLoading) {
    return (
      <div className={styles.loadingState}>
        <Spinner label="Loading annotation sets…" />
      </div>
    );
  }

  if (error !== null) {
    return (
      <Body1 className={styles.errorState}>
        Failed to load annotation sets: {error}
      </Body1>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.toolbar}>
        {onBack !== undefined && (
          <Button
            size="small"
            appearance="subtle"
            icon={<ArrowLeft16Regular />}
            onClick={onBack}
          >
            {BACK_TO_ANNOTATE_LABEL}
          </Button>
        )}
        {canAnnotate ? (
          <Button
            appearance="primary"
            size="small"
            icon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
          >
            New Annotation Set
          </Button>
        ) : (
          <Tooltip
            content={DATASET_ROLE_TOOLTIP_ANNOTATOR}
            relationship="description"
          >
            <Button
              appearance="primary"
              size="small"
              icon={<AddIcon />}
              disabledFocusable
            >
              New Annotation Set
            </Button>
          </Tooltip>
        )}
        {canAnnotate ? (
          <Button
            size="small"
            icon={<UploadIcon />}
            onClick={() => {
              setUploadError(null);
              setUploadOpen(true);
            }}
          >
            Upload Annotation
          </Button>
        ) : (
          <Tooltip
            content={DATASET_ROLE_TOOLTIP_ANNOTATOR}
            relationship="description"
          >
            <Button size="small" icon={<UploadIcon />} disabledFocusable>
              Upload Annotation
            </Button>
          </Tooltip>
        )}
        <Button
          size="small"
          icon={<DownloadIcon />}
          onClick={() => {
            setDownloadError(null);
            setDownloadOpen(true);
          }}
        >
          Download Annotation
        </Button>
      </div>

      {createError !== null && (
        <Body1 className={styles.errorState}>{createError}</Body1>
      )}

      {annotationSets.length === 0 ? (
        <AnnotationsEmptyState />
      ) : (
        <AnnotationSetsTable
          annotationSets={annotationSets}
          schemas={schemas}
          dataSetId={dataSetId}
          dataSetVersion={dataSetVersion}
          service={service}
          canAnnotate={canAnnotate}
          canPublish={canPublish}
          onRefresh={refresh}
        />
      )}

      <CreateAnnotationSetDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        schemas={schemas}
        isCreating={isCreating}
        onCreate={(name, schemaId, description) => {
          void handleCreate(name, schemaId, description);
        }}
      />

      <UploadAnnotationDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        onConfirm={(files, annotationSetName) => {
          void handleUploadConfirm(files, annotationSetName);
        }}
        isUploading={isUploading}
        uploadProgress={uploadProgress}
        uploadError={uploadError}
      />

      <DownloadAnnotationDialog
        open={downloadOpen}
        onOpenChange={setDownloadOpen}
        defaultVersion={defaultVersion}
        onConfirm={(version, annotationSetName) => {
          void handleDownloadConfirm(version, annotationSetName);
        }}
        isDownloading={isDownloading}
        downloadError={downloadError}
      />
    </div>
  );
}
